import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import TransactionsTable, { type TransactionRow } from '../TransactionsTable'

/**
 * No-signup canned demo. Replays the REAL converter output for a clearly-labelled
 * synthetic sample statement: the rows come from /demo/transactions.json and the
 * downloadable XLSX/CSV/JSON are the genuine ExportService output (so the table and
 * the files can never diverge). SSR-safe: initial state is deterministic and no
 * window/document/Date is touched during render; fetch + timers run only after the
 * user clicks "Run demo".
 */

const STEPS = [
  'Reading PDF pages…',
  'Extracting transactions…',
  'Reconciling running balances…',
  'Building Excel workbook…',
]
const STEP_MS = 550

type Status = 'idle' | 'processing' | 'done' | 'error'

const EXPORTS: { format: string; label: string; href: string }[] = [
  { format: 'xlsx', label: 'Excel (.xlsx)', href: '/demo/sample-bank-statement.xlsx' },
  { format: 'csv', label: 'CSV (.csv)', href: '/demo/sample-bank-statement.csv' },
  { format: 'json', label: 'JSON (.json)', href: '/demo/sample-bank-statement.json' },
]

export default function DemoConverter() {
  const [status, setStatus] = useState<Status>('idle')
  const [step, setStep] = useState(0)
  const [rows, setRows] = useState<TransactionRow[] | null>(null)
  const ctrlRef = useRef<AbortController | null>(null)

  // Abort any in-flight fetch on unmount.
  useEffect(() => () => ctrlRef.current?.abort(), [])

  const run = async () => {
    ctrlRef.current?.abort()
    const ctrl = new AbortController()
    ctrlRef.current = ctrl
    const timer = setTimeout(() => ctrl.abort(), 15000)
    setStatus('processing')
    setStep(0)
    setRows(null)
    try {
      const res = await fetch('/demo/transactions.json', { signal: ctrl.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const txns = (data.transactions ?? data) as TransactionRow[]
      if (!Array.isArray(txns) || txns.length === 0) throw new Error('no transactions in sample')
      setRows(txns)
    } catch {
      setRows(null)
      setStatus('error')
    } finally {
      clearTimeout(timer)
    }
  }

  // Stepper animation (effect only — no timers during render).
  useEffect(() => {
    if (status !== 'processing') return
    if (step >= STEPS.length) return
    const t = setTimeout(() => setStep((s) => s + 1), STEP_MS)
    return () => clearTimeout(t)
  }, [status, step])

  // Flip to results once both the animation finished and the data arrived.
  useEffect(() => {
    if (status === 'processing' && step >= STEPS.length && rows) setStatus('done')
  }, [status, step, rows])

  return (
    <div className="rounded-3xl border border-black/10 bg-surface p-6 sm:p-8">
      {status === 'idle' && (
        <div className="rounded-2xl border-2 border-dashed border-black/15 px-6 py-12 text-center">
          <p className="text-lg font-semibold text-ink m-0">sample-bank-statement.pdf</p>
          <p className="text-ink-soft mt-2 mb-6">See exactly what the converter returns — no signup, no upload.</p>
          <button
            onClick={run}
            className="px-6 py-2.5 rounded-full bg-accent text-[#06301c] font-semibold hover:bg-accent-dark hover:text-white transition"
          >
            Run demo
          </button>
        </div>
      )}

      {status === 'processing' && (
        <div className="px-6 py-12 text-center">
          <div className="mx-auto mb-5 h-6 w-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-ink font-medium m-0">{STEPS[Math.min(step, STEPS.length - 1)]}</p>
          <div className="mx-auto mt-4 w-64 h-2 bg-black/10 rounded overflow-hidden">
            <div
              className="h-2 bg-accent transition-all duration-500"
              style={{ width: `${Math.round((Math.min(step + 1, STEPS.length) / STEPS.length) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="px-6 py-12 text-center">
          <p className="text-ink m-0">Couldn’t load the demo sample.</p>
          <button onClick={run} className="mt-4 px-5 py-2 rounded-full bg-surface-muted text-ink font-semibold hover:bg-gray-200 transition">
            Try again
          </button>
        </div>
      )}

      {status === 'done' && rows && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Extracted transactions ({rows.length})</h3>
            <div className="flex flex-wrap gap-2">
              {EXPORTS.map((e) => (
                <a
                  key={e.format}
                  href={e.href}
                  download
                  className="px-4 py-2 rounded-full bg-surface-muted text-ink text-sm font-semibold hover:bg-gray-200 transition"
                >
                  {e.label}
                </a>
              ))}
            </div>
          </div>
          <TransactionsTable transactions={rows} />
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-black/5 pt-4">
        <p className="text-[0.8rem] text-ink-soft m-0">
          Synthetic sample — fictional account, for demonstration only.
        </p>
        <Link
          to="/sign-up"
          className="px-5 py-2 rounded-full bg-accent text-[#06301c] text-sm font-semibold hover:bg-accent-dark hover:text-white transition"
        >
          Upload your own statement →
        </Link>
      </div>
    </div>
  )
}
